import '../styles/expeditions.css';

import { store } from '../state';
import { i18n } from '../i18n';
import type { AppState } from '../types';
import { expeditionsAPI } from '../api';
import { renderTemplate } from '../template-engine';
import { buildTemplateData, formatters } from '../template-utils';

import expeditionCardTpl from '../templates/expedition-card.html?raw';

const els = {
  expeditionsListContainer: document.getElementById('expeditions-list-container') as HTMLElement,
  expeditionDetailsContainer: document.getElementById('expedition-details-container') as HTMLElement,
};

export function initEventListeners() {
  els.expeditionsListContainer.addEventListener('click', (e) => {
    // Явно приводим результат closest к HTMLElement, чтобы работал dataset
    const item = ((e.target as HTMLElement).closest('.expedition-list-item')) as HTMLElement | null;
    
    if (item?.dataset?.id) {
      const id = item.dataset.id;
      // Находим экспедицию в стейте и выбираем её
      const exp = store.getState().expeditionsList.find(e => e.id === id);
      if (exp) {
        store.dispatch({ type: 'SELECT_EXPEDITION', payload: exp });
      }
    }
  });
}

export async function loadExpeditions() {
  const state = store.getState();
  if (state.expeditionsList.length > 0 || state.expeditionsLoading) return;

  store.dispatch({ type: 'SET_EXPEDITIONS_LOADING', payload: true });
  try {
    const response = await expeditionsAPI.list({ limit: 100 });
    store.dispatch({ type: 'SET_EXPEDITIONS_LIST', payload: response.data });
    
    // АВТОВЫБОР: если ничего не выбрано, выбираем первый элемент
    if (!state.selectedExpedition && response.data.length > 0) {
      store.dispatch({ type: 'SELECT_EXPEDITION', payload: response.data[0] });
    }
  } catch (error) {
    console.error('Ошибка загрузки экспедиций:', error);
    store.dispatch({ type: 'SET_EXPEDITIONS_ERROR', payload: i18n.t('expeditions.error.load') });
  }
}

const formatDate = (isoString?: string | null) => {
  if (!isoString) return i18n.t('ui.not_specified');
  try { return new Date(isoString).toLocaleString(i18n.lang === 'ru' ? 'ru-RU' : 'en-US'); } 
  catch { return i18n.t('ui.unknown'); }
};


// НОВАЯ ФУНКЦИЯ: Рендер раздела экспедиций
export function renderExpeditions(state: AppState) {
  // 1. Рендер списка (левая колонка)
  if (state.expeditionsLoading) {
    els.expeditionsListContainer.innerHTML = `
      <div class="state-view loading" style="min-height: 150px;">
        <div class="spinner" style="width: 30px; height: 30px;"></div>
      </div>`;
  } else if (state.expeditionsError) {
    els.expeditionsListContainer.innerHTML = `<div class="state-view error"><p>${state.expeditionsError}</p></div>`;
  } else if (state.expeditionsList.length === 0) {
    els.expeditionsListContainer.innerHTML = `<div class="state-view empty"><p data-i18n="expeditions.empty_list">Список пуст</p></div>`;
  } else {
    els.expeditionsListContainer.innerHTML = state.expeditionsList.map(exp => {
      const isActive = state.selectedExpedition?.id === exp.id;
      const dateRange = `${formatDate(exp.date_start)} – ${formatDate(exp.date_end)}`;
      const regularBadge = exp.regular ? `<span class="badge badge-regular">${i18n.t('expeditions.regular')}</span>` : '';
      
      return `
        <div class="expedition-list-item ${isActive ? 'active' : ''}" data-id="${exp.id}">
          <div class="item-title">${escapeHtml(exp.name)}</div>
          <div class="item-meta">
            <span class="item-date">${dateRange}</span>
            ${regularBadge}
          </div>
        </div>
      `;
    }).join('');
  }

  // 2. Рендер деталей (правая колонка)
  if (!state.selectedExpedition) {
    els.expeditionDetailsContainer.innerHTML = `
      <div class="stub-content">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="color: var(--accent-color); margin-bottom: 16px;">
          <path d="M12 2L2 7l10 5 10-5-10-5z"></path><path d="M2 17l10 5 10-5"></path><path d="M2 12l10 5 10-5"></path>
        </svg>
        <h2 data-i18n="expeditions.select_prompt">Выберите экспедицию</h2>
        <p data-i18n="expeditions.select_prompt_desc">для просмотра подробной информации</p>
      </div>
    `;
  } else {
    const exp = state.selectedExpedition;
    const templateData = buildTemplateData(
      exp,
      {
        name: { key: 'name' },
        dateStart: { key: 'date_start', format: formatters.date },
        dateEnd: { key: 'date_end', format: formatters.date, fallback: i18n.t('ui.not_specified') },
        regular: { key: 'regular', format: (val) => !!val }, // для {{#if regular}}
        regularText: { key: 'regular', format: (val) => val ? i18n.t('expeditions.yes') : i18n.t('expeditions.no') },
        descriptionHtml: { key: 'description', format: formatters.pureHtml, fallback: null },
      },
      {
        i18n: {
          expeditions: {
            date_start: i18n.t('expeditions.date_start'),
            date_end: i18n.t('expeditions.date_end'),
            regular: i18n.t('expeditions.regular'),
            regular_flag: i18n.t('expeditions.regular_flag'),
            description: i18n.t('expeditions.description')
          }
        }
      }
    );

    els.expeditionDetailsContainer.innerHTML = renderTemplate(expeditionCardTpl, templateData);
  }
}

const escapeHtml = (str: string | undefined | null): string => {
  if (str == null) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
};

